// Table-shaped fallbacks: the present_table envelope, generic multi-column
// tables, and the entity-chip result for everything else.

import type {
  ActionResultData,
  BlockOutput,
  DataTableBlockData,
  EntityChipData,
  EntityKind,
} from '../../types';
import { pluralise } from '../pluralise';
import { arr, str, strArr } from './guards';
import { cell, colIndex, mapSuggestedActions } from './shared';
import type { StructuredDataTable, StructuredTable } from './types';

const ENTITY_KIND_MAP: Record<string, EntityKind> = {
  job: 'job',
  candidate: 'candidate',
  contact: 'candidate',
  application: 'candidate',
  user: 'user',
  stage: 'stage',
  department: 'department',
};

// Generic table from the present_table tool: arbitrary columns/rows, laid out
// as a real table without pattern-matching candidate fields.
export const buildDataTableBlock = (
  t: StructuredDataTable,
  toolCallId?: string
): BlockOutput | null => {
  const columns = strArr(t.columns);
  const rows = arr(t.rows)
    .map((r) =>
      Array.isArray(r)
        ? r.map((c) => (typeof c === 'string' ? c : String(c)))
        : null
    )
    .filter((r): r is string[] => r !== null);
  if (columns.length === 0 || rows.length === 0) return null;
  const ids = Array.isArray(t.ids) ? strArr(t.ids) : undefined;
  const rawVariant = str(t.variant) ?? 'list';
  const variant: DataTableBlockData['variant'] =
    rawVariant === 'ranking' ||
    rawVariant === 'comparison' ||
    rawVariant === 'list' ||
    rawVariant === 'summary'
      ? rawVariant
      : 'list';
  const data: DataTableBlockData = {
    intro: str(t.intro) ?? '',
    variant,
    columns,
    rows,
    ids: ids && ids.length > 0 ? ids : undefined,
    entity: t.entity === 'job' ? 'job' : 'contact',
    footnote: str(t.footnote),
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'dataTable', data };
};

// Multi-column tabular data with no bespoke entity block renders as a real
// table. Ids are dropped: dataTable links ids as contact/job records, and a
// generic entity's ids (stage ids, template ids) would link to the wrong place.
export const buildGenericTableBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = (t.rows ?? []).map((r) => r.map((c) => String(c ?? '')));
  const columns = (t.columns ?? []).map((c) => String(c));
  const data: DataTableBlockData = {
    intro: t.entity
      ? `${pluralise(rows.length, t.entity)}.`
      : `${rows.length} rows.`,
    variant: 'list',
    columns,
    rows,
    entity: 'contact',
    footnote: str(t.footnote),
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'dataTable', data };
};

// Anything with no bespoke block yet (application lists, name lists…) becomes
// an actionResult with chips per row: the user still sees the data, the LLM
// still summarizes in prose.
export const buildActionResultBlock = (
  t: StructuredTable,
  toolCallId?: string
): BlockOutput => {
  const rows = t.rows ?? [];
  const ids = t.ids ?? [];
  const total = t.total ?? rows.length;
  const kind: EntityKind = ENTITY_KIND_MAP[t.entity ?? ''] ?? 'job';
  const entities: EntityChipData[] = rows.map((r, i) => ({
    kind,
    id: ids[i] ?? String(i),
    label: cell(r, colIndex(t.columns, 'Name')) || `#${ids[i] ?? i}`,
  }));
  const counted = pluralise(total, t.entity ?? 'result');
  const data: ActionResultData = {
    summary:
      total > rows.length
        ? `${counted} — showing top ${rows.length}.`
        : `${counted}.`,
    entities,
    suggestedActions: mapSuggestedActions(t.suggested_actions),
    toolCallId,
  };
  return { type: 'actionResult', data };
};
